import {
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { DataTable } from '../components/datatables/datatable';
import DatatableSortButton from '../components/datatables/datatableSortButton';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import api from '../lib/api';

export const columns = () => [
    {
        accessorKey: "fullName",
        header: ({ column }) => <DatatableSortButton column={column}>Namn</DatatableSortButton>,
        cell: info => {
            const bunny = info.row.original;
            return <Link to={`/bunny/${bunny.bunnyId}`} className='text-blue-600 font-medium hover:underline'>{bunny.fullName}</Link>
        }
    },
    {
        accessorKey: "certificateCount",
        header: ({ column }) => <DatatableSortButton column={column}>Antal certfikat</DatatableSortButton>,
        cell: info => {
            const bunny = info.row.original;
            return bunny.certificateCount;
        }
    },
];

const BunnyRankings = () => {
    const [rankings, setRankings] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [sorting, setSorting] = useState([{ id: 'certificateCount', desc: true }]);
    const [columnFilters, setColumnFilters] = useState([]);
    const [filterInput, setFilterInput] = useState("");
    const [searchYear, setSearchYear] = useState(new Date().getFullYear());

    const fetchRankings = useCallback( async () => {
        if (searchYear === null) return;
        try {
            setIsLoading(true);
            const response = await api.get(`https://skhrf-api.cmdigital.se/v1/bunnies/rankings?year=${searchYear}`);

            // Sort rankings by certificateCount in descending order
            // const sortedRankings = response.data.sort((a, b) => b.certificateCount - a.certificateCount);
            // setRankings(sortedRankings);
            setRankings(response.data)
            setIsLoading(false);
        } catch (error) {
            console.error('Error fetching rankings:', error);
            setRankings([]);
        } finally {
            setIsLoading(false);
        }
    }, [searchYear]);

    useEffect(() => {
        fetchRankings();
    }, [fetchRankings]);

    const table = useReactTable({
        data: rankings,
        columns: columns(),
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        onSortingChange: setSorting,
        getSortedRowModel: getSortedRowModel(),
        onColumnFiltersChange: setColumnFilters,
        getFilteredRowModel: getFilteredRowModel(),
        state: {
            sorting,
            columnFilters,
        },
    });

    // Delay function before filtering to reduce bounce
    useEffect(() => {
        const handler = setTimeout(() => {
            table.getColumn("fullName")?.setFilterValue(filterInput);
        }, 500);

        return () => {
            clearTimeout(handler);
        };
    }, [filterInput, table]);

    // Generate years dynamically
    const generateYears = () => {
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let year = 2022; year <= currentYear; year++) {
            years.push(year);
        }
        return years;
    };

    return (
        <div className='flex w-full items-center justify-center'>
            <div className='flex w-5/6 sm:w-3/4 flex-col py-6 gap-3'>
                <h1 className='font-medium text-2xl'>Rankinglistor</h1>
                <p className='text-muted-foreground text-sm'>Här visas alla kaniner som tagit certifikat ordnade efter antal tagna certifikat.</p>
                <div className='grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3 py-3'>
                    <div className='flex flex-col gap-2'>
                        <Label htmlFor='searchBunny'>Kanin</Label>
                        <Input
                            id='searchBunny'
                            placeholder="Sök kanin..."
                            value={filterInput}
                            disabled={isLoading}
                            onChange={event => setFilterInput(event.target.value)}
                            className="w-full"
                        />
                    </div>
                    <div className='flex flex-col gap-2'>
                        <Label htmlFor='searchYear'>År</Label>
                        <Select defaultValue={searchYear.toString()} onValueChange={value => setSearchYear(parseInt(value))} disabled={isLoading} >
                            <SelectTrigger className="w-full" id='searchYear'>
                                <SelectValue placeholder="Välj år..." />
                            </SelectTrigger>
                            <SelectContent>
                                {generateYears().map(year => (
                                    <SelectItem key={year} value={year.toString()}>
                                        {year}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <DataTable
                    table={table}
                    isLoading={isLoading}
                    columns={columns()}
                    skeletonRows={10}
                />
            </div>
        </div>
    );
};

export default BunnyRankings;
